import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LayoutDashboard, Package, MessageSquare, TrendingUp, User, LogOut, Settings, Mail, Bot, BarChart3, Eye, ChevronDown, KeyRound, Building2, Sparkles, Menu, X, ShoppingBag, Home, Trash2, AlertTriangle, Globe } from 'lucide-react';
import { toast } from 'sonner';
import NotificationDropdown from '@/components/NotificationDropdown';
import LanguageSelector from '@/components/LanguageSelector';
import HelpChatbot from '@/components/HelpChatbot';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { FullyVerifiedBadge } from '@/components/VerificationBadges';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [aiDraftCount, setAiDraftCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState({
    phone_verified: false,
    gstin_verified: false,
    iec_status: 'not_submitted'
  });
  const { t } = useLanguage();
  
  // Delete account state
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountReason, setDeleteAccountReason] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    
    // Fetch AI draft count for sellers
    if (userData?.role === 'seller') {
      fetchAiDraftCount();
      fetchVerificationStatus();
    }
  }, [location]);

  const fetchVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setVerificationStatus({
          phone_verified: response.data.phone_verified || false,
          gstin_verified: response.data.gstin_verified || false,
          iec_status: response.data.iec_status || 'not_submitted'
        });
      }
    } catch (error) {
      console.error('Failed to fetch verification status');
    }
  };

  const fetchAiDraftCount = async () => {
    try {
      const response = await axios.get(`${API}/ai-drafts/count`);
      setAiDraftCount(response.data.pending || 0);
    } catch (error) {
      console.error('Failed to fetch AI draft count');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success(t.toast.logoutSuccess);
    navigate('/auth');
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountPassword) {
      toast.error('Please enter your password to confirm');
      return;
    }
    
    setDeletingAccount(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/delete-account`, {
        password: deleteAccountPassword,
        reason: deleteAccountReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setShowDeleteAccountDialog(false);
      navigate('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const isSeller = user?.role === 'seller';
  const isBuyer = user?.role === 'buyer';

  // Primary nav items (always visible)
  const primaryNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t.nav.dashboard },
    { path: '/products', icon: Package, label: t.nav.products },
    { path: '/inquiries', icon: MessageSquare, label: t.nav.inquiries },
    { path: '/leads', icon: TrendingUp, label: t.nav.leads },
  ];

  // AI & Tools dropdown items (sellers only)
  const aiToolsItems = [
    { path: '/ai-drafts', icon: Bot, label: t.nav.aiDrafts, badge: aiDraftCount },
    { path: '/market-trends', icon: BarChart3, label: t.nav.trends },
    { path: '/export-tools', icon: Globe, label: 'Export Tools' },
    { path: '/followups', icon: Mail, label: t.nav.followUps },
  ];

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer flex-shrink-0" onClick={() => navigate('/dashboard')} data-testid="nav-logo">
              <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-9 w-auto" />
            </div>
            
            <div className="hidden md:flex items-center gap-1">
              {/* Primary Navigation */}
              {primaryNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => navigate(item.path)}
                    className="gap-1.5"
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}-btn`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden lg:inline">{item.label}</span>
                  </Button>
                );
              })}

              {/* Buyer Watchlist */}
              {isBuyer && (
                <Button
                  variant={location.pathname === '/buyer-dashboard' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => navigate('/buyer-dashboard')}
                  className="gap-1.5"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden lg:inline">Watchlist</span>
                </Button>
              )}
              
              {/* AI & Tools Dropdown (Sellers only) */}
              {isSeller && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1.5">
                      <Sparkles className="h-4 w-4" />
                      <span className="hidden lg:inline">AI Tools</span>
                      {aiDraftCount > 0 && (
                        <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center bg-amber-500 text-xs">
                          {aiDraftCount}
                        </Badge>
                      )}
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>AI & Automation</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {aiToolsItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <DropdownMenuItem 
                          key={item.path} 
                          onClick={() => navigate(item.path)}
                          className="cursor-pointer"
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {item.label}
                          {item.badge > 0 && (
                            <Badge className="ml-auto h-5 px-1.5 bg-amber-500 text-xs">
                              {item.badge}
                            </Badge>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Settings (Sellers only) */}
              {isSeller && (
                <Button
                  variant={location.pathname === '/settings' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => navigate('/settings')}
                  className="gap-1.5"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
              
              {/* Notification bell for sellers */}
              {isSeller && <NotificationDropdown />}
              
              {/* Language Selector */}
              <LanguageSelector variant="ghost" />
              
              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.company_logo} alt={user?.company_name} />
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {user?.company_name?.charAt(0) || user?.full_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden lg:flex flex-col items-start text-left">
                      <span className="text-sm font-medium truncate max-w-[120px] flex items-center gap-1.5">
                        {user?.company_name || user?.full_name || 'User'}
                        {/* Verified Seller Badge - only for sellers with all 3 verifications */}
                        {isSeller && (
                          <FullyVerifiedBadge 
                            phoneVerified={verificationStatus.phone_verified}
                            gstinVerified={verificationStatus.gstin_verified}
                            iecStatus={verificationStatus.iec_status}
                            size="small"
                            variant="compact"
                            showLabel={false}
                          />
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">{user?.role}</span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-2 flex items-center gap-2">
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1.5">
                        {user?.company_name || user?.full_name}
                        {/* Verified badge in dropdown too */}
                        {isSeller && (
                          <FullyVerifiedBadge 
                            phoneVerified={verificationStatus.phone_verified}
                            gstinVerified={verificationStatus.gstin_verified}
                            iecStatus={verificationStatus.iec_status}
                            size="small"
                            variant="compact"
                            showLabel={false}
                          />
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <User className="h-4 w-4 mr-2" />
                    {t.nav.profile}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile?tab=password')} className="cursor-pointer">
                    <KeyRound className="h-4 w-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t.nav.logout}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteAccountDialog(true)} 
                    className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="md:hidden flex items-center gap-2">
              {isSeller && <NotificationDropdown />}
              <LanguageSelector variant="ghost" showLabel={false} />
              
              {/* Mobile Menu Button */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="px-2" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] sm:w-[320px]">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user?.company_logo} alt={user?.company_name} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {user?.company_name?.charAt(0) || user?.full_name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="text-sm font-medium truncate flex items-center gap-1.5">
                          {user?.company_name || user?.full_name}
                          {/* Verified badge on mobile menu */}
                          {isSeller && (
                            <FullyVerifiedBadge 
                              phoneVerified={verificationStatus.phone_verified}
                              gstinVerified={verificationStatus.gstin_verified}
                              iecStatus={verificationStatus.iec_status}
                              size="small"
                              variant="compact"
                              showLabel={false}
                            />
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                      </div>
                    </SheetTitle>
                  </SheetHeader>
                  
                  <div className="mt-6 flex flex-col gap-1">
                    {/* Main Navigation */}
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                      Navigation
                    </p>
                    
                    {/* Marketplace Link */}
                    <Button
                      variant={location.pathname === '/marketplace' ? 'secondary' : 'ghost'}
                      className="w-full justify-start gap-3"
                      onClick={() => { navigate('/marketplace'); setMobileMenuOpen(false); }}
                      data-testid="mobile-nav-marketplace"
                    >
                      <ShoppingBag className="h-5 w-5" />
                      Marketplace
                    </Button>
                    
                    {primaryNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Button
                          key={item.path}
                          variant={isActive ? 'secondary' : 'ghost'}
                          className="w-full justify-start gap-3"
                          onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        >
                          <Icon className="h-5 w-5" />
                          {item.label}
                        </Button>
                      );
                    })}
                    
                    {/* Buyer Watchlist */}
                    {isBuyer && (
                      <Button
                        variant={location.pathname === '/buyer-dashboard' ? 'secondary' : 'ghost'}
                        className="w-full justify-start gap-3"
                        onClick={() => { navigate('/buyer-dashboard'); setMobileMenuOpen(false); }}
                      >
                        <Eye className="h-5 w-5" />
                        Watchlist
                      </Button>
                    )}
                    
                    {/* AI & Tools Section (Sellers only) */}
                    {isSeller && (
                      <>
                        <div className="my-3 border-t" />
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                          AI & Tools
                        </p>
                        {aiToolsItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Button
                              key={item.path}
                              variant={location.pathname === item.path ? 'secondary' : 'ghost'}
                              className="w-full justify-start gap-3"
                              onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                              data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            >
                              <Icon className="h-5 w-5" />
                              {item.label}
                              {item.badge > 0 && (
                                <Badge className="ml-auto h-5 px-1.5 bg-amber-500 text-xs">
                                  {item.badge}
                                </Badge>
                              )}
                            </Button>
                          );
                        })}
                      </>
                    )}
                    
                    {/* Settings & Account Section */}
                    <div className="my-3 border-t" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                      Account
                    </p>
                    
                    <Button
                      variant={location.pathname === '/profile' ? 'secondary' : 'ghost'}
                      className="w-full justify-start gap-3"
                      onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                      data-testid="mobile-nav-profile"
                    >
                      <User className="h-5 w-5" />
                      Profile
                    </Button>
                    
                    {isSeller && (
                      <Button
                        variant={location.pathname === '/settings' ? 'secondary' : 'ghost'}
                        className="w-full justify-start gap-3"
                        onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}
                        data-testid="mobile-nav-settings"
                      >
                        <Settings className="h-5 w-5" />
                        Settings
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3"
                      onClick={() => { navigate('/profile?tab=password'); setMobileMenuOpen(false); }}
                    >
                      <KeyRound className="h-5 w-5" />
                      Change Password
                    </Button>
                    
                    <div className="my-3 border-t" />
                    
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      data-testid="mobile-nav-logout"
                    >
                      <LogOut className="h-5 w-5" />
                      Logout
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      <main className="pb-16 md:pb-0">{children}</main>
      
      {/* Mobile Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-border z-50 safe-area-bottom">
        <div className="flex items-center justify-around h-14">
          <Button
            variant="ghost"
            size="sm"
            className={`flex-1 flex flex-col items-center gap-0.5 h-full rounded-none ${location.pathname === '/dashboard' ? 'text-primary bg-primary/5' : 'text-muted-foreground'}`}
            onClick={() => navigate('/dashboard')}
            data-testid="bottom-nav-dashboard"
          >
            <LayoutDashboard className="h-5 w-5" />
            <span className="text-[10px]">Dashboard</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className={`flex-1 flex flex-col items-center gap-0.5 h-full rounded-none ${location.pathname === '/marketplace' ? 'text-primary bg-primary/5' : 'text-muted-foreground'}`}
            onClick={() => navigate('/marketplace')}
            data-testid="bottom-nav-marketplace"
          >
            <ShoppingBag className="h-5 w-5" />
            <span className="text-[10px]">Marketplace</span>
          </Button>
          
          {isSeller && (
            <Button
              variant="ghost"
              size="sm"
              className={`flex-1 flex flex-col items-center gap-0.5 h-full rounded-none ${location.pathname === '/products' ? 'text-primary bg-primary/5' : 'text-muted-foreground'}`}
              onClick={() => navigate('/products')}
              data-testid="bottom-nav-products"
            >
              <Package className="h-5 w-5" />
              <span className="text-[10px]">Products</span>
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            className={`flex-1 flex flex-col items-center gap-0.5 h-full rounded-none ${location.pathname === '/inquiries' ? 'text-primary bg-primary/5' : 'text-muted-foreground'}`}
            onClick={() => navigate('/inquiries')}
            data-testid="bottom-nav-inquiries"
          >
            <MessageSquare className="h-5 w-5" />
            <span className="text-[10px]">Inquiries</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className={`flex-1 flex flex-col items-center gap-0.5 h-full rounded-none ${location.pathname === '/profile' ? 'text-primary bg-primary/5' : 'text-muted-foreground'}`}
            onClick={() => navigate('/profile')}
            data-testid="bottom-nav-profile"
          >
            <User className="h-5 w-5" />
            <span className="text-[10px]">Profile</span>
          </Button>
        </div>
      </nav>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteAccountDialog} onOpenChange={setShowDeleteAccountDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Your Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              This action is <strong>permanent and cannot be undone</strong>. All your data will be deleted, including:
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                {isSeller ? (
                  <>
                    <li>All your products</li>
                    <li>All inquiries and leads</li>
                    <li>AI drafts and conversation history</li>
                  </>
                ) : (
                  <>
                    <li>All your inquiries</li>
                    <li>Your watchlist and price alerts</li>
                  </>
                )}
                <li>Your profile and account information</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="delete-password">Enter your password to confirm</Label>
              <Input
                id="delete-password"
                type="password"
                placeholder="Your password"
                value={deleteAccountPassword}
                onChange={(e) => setDeleteAccountPassword(e.target.value)}
                data-testid="delete-account-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="delete-reason">Reason for leaving (optional)</Label>
              <Textarea
                id="delete-reason"
                placeholder="Help us improve by sharing why you're leaving..."
                value={deleteAccountReason}
                onChange={(e) => setDeleteAccountReason(e.target.value)}
                rows={2}
                data-testid="delete-account-reason"
              />
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel 
              disabled={deletingAccount}
              onClick={() => {
                setDeleteAccountPassword('');
                setDeleteAccountReason('');
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deletingAccount || !deleteAccountPassword}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingAccount ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete My Account
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Footer */}
      <Footer />
      
      {/* Help Chatbot - Floating */}
      <HelpChatbot />
    </div>
  );
}